// src/lib/i18n/config.ts
// Les langues du site, et les fonctions pures qui manient les adresses.
//
// ── Le parti pris (11/10/2026, décidé avec David) ──────────────────────────────────────
//
// La langue vit dans l'URL, PARTOUT, français compris : `/fr/acteurs/x`, `/en/acteurs/x`.
// Le site va quitter son verrou de connexion et devenir public : une URL par langue est ce
// qui permet d'indexer chaque version, de partager un lien qui s'ouvre dans la bonne langue
// et de déclarer `hreflang`. Préfixer aussi le français évite le cas particulier « le
// français n'a pas de préfixe » dans tout le code, et c'est le moment le moins cher pour le
// faire : tant que le verrou tient, aucune adresse n'est indexée.
//
// Les slugs restent IDENTIQUES d'une langue à l'autre : on traduit le contenu, jamais les
// adresses. Ni l'IP ni `Accept-Language` ne forcent la langue : ils ne servent qu'à choisir
// où mène `/` pour un humain, et à suggérer un changement.
//
// Rien ici n'importe Next : ces fonctions sont testées sans serveur.

export const LOCALES = ['fr', 'en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'fr';

/** Cookie de préférence : posé quand le lecteur change de langue ou visite une URL préfixée. */
export const LANG_COOKIE = 'ln_lang';
/** En-tête posé par le proxy sur la requête, lu côté serveur par `getLang()`. */
export const LANG_HEADER = 'x-lang';

/**
 * Le routage par langue est-il actif ? Éteint par défaut : tant qu'il l'est, le site se
 * comporte exactement comme avant (aucune adresse préfixée, aucune redirection). Posé en
 * `NEXT_PUBLIC_` pour que le serveur ET les composants clients lisent la même valeur.
 */
export function routageActif(): boolean {
  return process.env.NEXT_PUBLIC_I18N_ROUTING === 'on';
}

export function estLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/** Libellés des langues, dans leur propre langue : un lecteur reconnaît la sienne. */
export const NOMS_DES_LANGUES: Record<Locale, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
};

/**
 * Chemins qui n'ont PAS de version par langue : l'API, les ressources de Next, et l'admin
 * (l'interface d'administration reste en français ; ce que l'admin édite, lui, est traduit).
 */
const SANS_LANGUE = ['/api', '/_next', '/admin'];

/** Un fichier (favicon, image, plan du site...) n'a pas de langue non plus. */
const FICHIER = /\.[a-z0-9]{2,5}$/i;

export function estSansLangue(pathname: string): boolean {
  if (SANS_LANGUE.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true;
  return FICHIER.test(pathname.split('?')[0]);
}

/** Sépare `/en/acteurs/x` en `{ locale: 'en', reste: '/acteurs/x' }`. */
export function separerLangue(pathname: string): { locale: Locale | null; reste: string } {
  const m = pathname.match(/^\/([a-z]{2})(\/.*)?$/i);
  if (m && estLocale(m[1].toLowerCase())) {
    return { locale: m[1].toLowerCase() as Locale, reste: m[2] && m[2] !== '/' ? m[2] : '/' };
  }
  return { locale: null, reste: pathname };
}

/** Le chemin sans son préfixe de langue : sert à reconnaître la page active d'une navigation. */
export function sansLangue(pathname: string): string {
  return separerLangue(pathname).reste;
}

/**
 * Préfixe une adresse INTERNE de la langue. Laisse intacts : les adresses externes, `mailto:`,
 * les ancres seules, les adresses déjà préfixées, et les chemins sans langue.
 *
 *   localiser('/acteurs/x?tab=bs#a', 'en')  → '/en/acteurs/x?tab=bs#a'
 *   localiser('https://exemple.org', 'en')  → inchangé
 */
export function localiser(href: string, locale: Locale): string {
  if (!href.startsWith('/') || href.startsWith('//')) return href;
  if (!routageActif()) return href;

  const chemin = href.split(/[?#]/)[0];
  if (estSansLangue(chemin)) return href;
  if (separerLangue(chemin).locale) return href;

  return `/${locale}${href === '/' ? '' : href}`;
}

/**
 * La meilleure langue d'un en-tête `Accept-Language` parmi les nôtres, ou `null`.
 * « fr-CH, fr;q=0.9, en;q=0.8 » → 'fr'. Les poids décident, pas l'ordre.
 */
export function negocier(acceptLanguage: string | null | undefined): Locale | null {
  if (!acceptLanguage) return null;
  const candidats = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      return { base: tag.trim().toLowerCase().split('-')[0], q: q ? Number(q.slice(2)) : 1 };
    })
    .filter((c) => c.base && Number.isFinite(c.q) && c.q > 0)
    .sort((a, b) => b.q - a.q);

  for (const c of candidats) if (estLocale(c.base)) return c.base;
  return null;
}
